/* eslint-disable complexity */

import { useState } from 'react';
import { SelectMenu, Button, Tabs, Typography } from '@scale/scaleui';
import {
  InteractionStepType,
  DEFAULT_PROCESS_SUPERVISION_TITLE,
  DEFAULT_PROCESS_SUPERVISION_END_AFTER_RATINGS_TITLE,
} from '@scale/shared/types/chat';
import BooleanInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/BooleanInputBuilder';
import StringInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/StringInputBuilder';
import {
  DEFAULT_PROCESS_CHUNKING_STRATEGY,
  DEFAULT_RATING_BUTTONS,
  DEFAULT_FIELDS_TO_COLLECT,
} from './types';
import {
  ProcessChunkingStrategy,
  ProcessChunkingStrategyMap,
  ProcessChunkingStrategyParamsMap,
} from './chunking';
import { ConditionalStepConfig } from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/configs';
import { ZodFormBuilder } from 'react-scale/corp/views/CorpProjectGroupManager/views/Linter/GenericLinterView/ZodFormBuilder';
import JSONInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/JSONInputBuilder';

const ConfigTabs = {
  General: 'General',
  Chunking: 'Chunking',
  Labels: 'Labels',
  Prerating: 'Prerating',
  Advanced: 'Advanced',
  ConditionalVisibility: 'Conditional Visibility',
};
import {
  PreratingStrategySchema,
  PreratingStrategyMap,
  PreratingStrategyParamsSchemaMap,
  PreratingReturnValueSchemaMap,
} from './prerate';
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd';

import { z } from 'zod';
import { StepConfigurationComponentProps } from '../stepDescriptor/types';
import { ExperimentalFeature } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ChatConfigurationEditor/components/ConfigLifeCycleInfo';

const RatingButtonsSchema = z.array(
  z.object({
    value: z.number(),
    label: z.string(),
    color: z.string().optional(),
  }),
);

const FieldsToCollectSchema = z.array(
  z.object({
    fieldId: z.string(),
    label: z.string(),
    description: z.string(),
    collectForRatingValues: z.array(z.number()),
    required: z.boolean(),
    type: z.enum(['markdown', 'latex', 'plaintext', 'select', 'multiselect']),
    options: z
      .array(
        z.object({
          value: z.string(),
          label: z.string(),
        }),
      )
      .optional(),
  }),
);

type ConfigTab = keyof typeof ConfigTabs;

export function ProcessSupervisionConfigurationComponent({
  params: currentParams,
  setParams: setCurrentParams,
  previousParams,
  taskParams,
}: StepConfigurationComponentProps<InteractionStepType.ProcessSupervision>) {
  const [tab, setTab] = useState<ConfigTab>('General');

  const strategyName = (currentParams.params.process_chunking_strategy ||
    DEFAULT_PROCESS_CHUNKING_STRATEGY) as ProcessChunkingStrategy;

  return (
    <div className="flex flex-col gap-4">
      <Tabs
        tabs={Object.entries(ConfigTabs).map(([tab, id]) => ({ id, name: tab }))}
        value={tab}
        onChange={t => setTab(t as ConfigTab)}
        variant="default"
      />

      {tab === 'General' && (
        <>
          <Typography.H3>General Configuration</Typography.H3>
          <Typography.Paragraph2 className="">
            Configure the basic settings for the Process Supervision step.
          </Typography.Paragraph2>

          <StringInputBuilder
            name={'Title'}
            placeholder={DEFAULT_PROCESS_SUPERVISION_TITLE}
            existingValue={currentParams.title}
            updateProperty={(value: string) => setCurrentParams({ ...currentParams, title: value })}
            required={true}
            hint="The title displayed for this Process Supervision step"
          />

          <Typography.Paragraph2>
            {' '}
            The following options allow you to configure an initial response, which will prefill the
            Process Supervision step, instead of having the response be generated by a Response
            Selector for turn 1. You can also specify the initial prompt that the response
            corresponds to, so that model generations on future turns will know what question was
            asked.{' '}
          </Typography.Paragraph2>

          <BooleanInputBuilder
            name={'Start From Scratch'}
            existingValue={currentParams.params.start_blank || false}
            updateProperty={(value: boolean) =>
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, start_blank: value },
              })
            }
            required={false}
            hint="Enable this to allow contributors to construct a chunked response from scratch"
          />

          {!currentParams.params.start_blank && (
            <>
              <StringInputBuilder
                name={'Initial response'}
                placeholder={'Optional initial prefilled response (string or {{variable}})'}
                existingValue={currentParams.params.initial_input || ''}
                updateProperty={(value: string) =>
                  setCurrentParams({
                    ...currentParams,
                    params: { ...currentParams.params, initial_input: value },
                  })
                }
                required={false}
                hint="Prefilled response to start with (can use template variables)"
              />
            </>
          )}

          {currentParams.params.initial_input && (
            <StringInputBuilder
              name={'[ADVANCED] Initial prompt'}
              placeholder={
                'Optional initial prompt that the prefilled response is for (string or {{variable}})'
              }
              existingValue={currentParams.params.initial_prompt || ''}
              updateProperty={(value: string) =>
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, initial_prompt: value },
                })
              }
              required={false}
              hint="The prompt that corresponds to the initial prefilled response"
            />
          )}

          <Typography.Paragraph2>
            {' '}
            You can specify which step ID the Process Supervision step pulls its content from. By
            default, Process Supervision uses the most recent Model Response Selector step at each
            turn. You may want to use this if you have multiple Response Selectors, want to pull
            from a Response Editor step, or other use cases.{' '}
          </Typography.Paragraph2>
          {previousParams?.length && (
            <SelectMenu
              label={`Select Input Step`}
              options={(previousParams || []).map(item => ({
                id: item.id,
                name: item.id + ' (' + item.type + ')',
              }))}
              selectedOptionId={currentParams.params.input_step_id || ''}
              setSelected={(id: string) =>
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, input_step_id: id },
                })
              }
            />
          )}
        </>
      )}

      {tab === 'Chunking' && (
        <>
          <Typography.H3>Chunking Configuration</Typography.H3>
          <Typography.Paragraph2 className="">
            Configure how the process is divided into chunks for supervision. This determines how
            the contributor sees chunks to annotate. You should use chunking strategies that make
            sense for your use case: ChunkBySentence will split by newlines in the content,
            ChunkByGPT is a slower and less-deterministic way to ask an LLM to generate logical
            chunks, ChunkByRegex can be used to write custom regex splits, etc. Make sure to test on
            various use cases in the Preview tab.
          </Typography.Paragraph2>

          <SelectMenu
            label={`Select Process Chunking Strategy`}
            options={Object.keys(ProcessChunkingStrategyMap).map(key => ({
              id: key,
              name: key,
            }))}
            selectedOptionId={strategyName}
            setSelected={id =>
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, process_chunking_strategy: id },
              })
            }
          />
          <Typography.Paragraph3 className="italic">
            {ProcessChunkingStrategyParamsMap[strategyName].description}
          </Typography.Paragraph3>

          {ProcessChunkingStrategyMap[strategyName]?.length > 1 && (
            <>
              <ZodFormBuilder
                label={'Process Chunking Strategy Params'}
                schema={ProcessChunkingStrategyParamsMap[strategyName]}
                value={currentParams.params.process_chunking_strategy_params}
                onChange={(value: any) => {
                  setCurrentParams({
                    ...currentParams,
                    params: { ...currentParams.params, process_chunking_strategy_params: value },
                  });
                }}
              />
            </>
          )}
          {/* <StringInputBuilder
            name={'Edit Chunks Title'}
            placeholder={DEFAULT_PROCESS_SUPERVISION_EDIT_CHUNKS_TITLE}
            existingValue={currentParams.params.edit_chunks_title}
            updateProperty={(value: string) =>
              setCurrentParams({
                ...currentParams,
                params: {
                  ...currentParams.params,
                  edit_chunks_title: value,
                },
              })
            }
            required={true}
            hint="Title displayed when editing chunks"
          /> */}

          <Typography.Paragraph2>
            {' '}
            By default, users can also edit chunks if they look incorrect. This has built-in
            validation to ensure they do not add or delete content from the original content. You
            can either disable editing entirely, or disable the validations so they can more freely
            edit.{' '}
          </Typography.Paragraph2>

          <BooleanInputBuilder
            name={'Disable the ability to edit chunks'}
            existingValue={currentParams.params.disable_edit_chunks ?? false}
            updateProperty={(value: any) => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, disable_edit_chunks: value },
              });
            }}
            required={false}
            hint="If enabled, users cannot edit chunks"
          />

          <BooleanInputBuilder
            name={'Disable manual chunk validation'}
            existingValue={currentParams.params.disable_manual_chunk_validation ?? false}
            updateProperty={(value: any) => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, disable_manual_chunk_validation: value },
              });
            }}
            required={false}
            hint="If enabled, manual chunk validation will be disabled"
          />
        </>
      )}

      {tab === 'Labels' && (
        <>
          <Typography.H3>Labels and Fields Configuration</Typography.H3>
          <Typography.Paragraph2 className="">
            Configure rating buttons and various fields to collect.{' '}
          </Typography.Paragraph2>
          <Typography.Paragraph2>
            Rating buttons correspond to the ratings that contributors can mark for each chunk.
          </Typography.Paragraph2>

          <ZodFormBuilder
            label={'Rating Buttons'}
            schema={RatingButtonsSchema}
            value={currentParams.params.rating_buttons || DEFAULT_RATING_BUTTONS}
            onChange={(value: any) => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, rating_buttons: value },
              });
            }}
          />
          <div className="flex flex-row gap-4 ">
            {(currentParams.params.rating_buttons || DEFAULT_RATING_BUTTONS)?.map((button: any) => (
              <Button key={button.label} variant={button.color} size="sm">
                {button.label} ({button.value})
              </Button>
            ))}
          </div>

          <Typography.Paragraph2>
            {' '}
            Fields to collect are used to collect additional information from contributors, like
            rewrites and justifications. You can specify which fields to collect, what type of input
            they should get, and which ratings (corresponding to the button values above) they
            should be collected for. The `collectForRatingValues` array specifies which ratings the
            field is collected for. If it is empty, the field is collected for all ratings.{' '}
          </Typography.Paragraph2>
          <ZodFormBuilder
            label={'Fields to Collect'}
            schema={FieldsToCollectSchema}
            value={currentParams.params.fields_to_collect || DEFAULT_FIELDS_TO_COLLECT}
            onChange={(value: any) => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, fields_to_collect: value },
              });
            }}
          />
          <div className="flex flex-col gap-4 ">
            {currentParams.params.fields_to_collect?.map((field: any) => (
              <div key={field.fieldId} className="flex flex-row justify-between items-center">
                <span>
                  {field.label} ({field.type})
                </span>
                <span className="flex flex-row gap-1 items-center">
                  Render for values:
                  {field.collectForRatingValues?.map((ratingValue: number) => (
                    <span key={ratingValue}>{ratingValue}</span>
                  ))}
                </span>
              </div>
            ))}
          </div>

          {/* <NumberInputBuilder
            name={'Min Negative Ratings'}
            existingValue={currentParams.params.min_negative_ratings}
            updateProperty={(value: number) => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, min_negative_ratings: value },
              });
            }}
            hint="Minimum number of negative ratings required"
          /> */}
        </>
      )}
      {/* TODO make this easier to configure and add warnings if misconfigured (e.g. render for values don't match rating buttons) */}
      {tab === 'Prerating' && (
        <>
          <Typography.H3>Prerating Configuration</Typography.H3>
          <Typography.Paragraph2 className="">
            Configure how each step is pre-rated, which means a rating is generated by default.
            These functions run on each chunk to generate ratings, and functions at the top will be
            prioritized over ones on the bottom. For example, matchesRegex will take in a regex term
            and a valueToRatingMapping which maps the output values of that function (true or false)
            to some rating button ID (e.g. -1) that you configured on the "Labels" tab.
          </Typography.Paragraph2>
          <>
            <DragDropContext
              onDragEnd={(result: DropResult) => {
                if (!result.destination) return;
                const newStrategies = Array.from(currentParams.params.prerating_strategies || []);
                const [reorderedItem] = newStrategies.splice(result.source.index, 1);
                newStrategies.splice(result.destination.index, 0, reorderedItem);
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, prerating_strategies: newStrategies },
                });
              }}
            >
              <Droppable droppableId="prerating-strategies">
                {(provided, _snapshot) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="flex flex-col gap-4"
                  >
                    {(currentParams.params.prerating_strategies || []).map(
                      (strategy: z.infer<typeof PreratingStrategySchema>, index: number) => (
                        <Draggable key={index} draggableId={`strategy-${index}`} index={index}>
                          {(provided, _snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <ZodFormBuilder
                                label={strategy.ruleName || `Prerating Strategy ${index + 1}`}
                                schema={PreratingStrategySchema.extend({
                                  params:
                                    PreratingStrategyParamsSchemaMap[strategy.ruleName] || z.any(),
                                  valueToRatingMapping: z.array(
                                    z.object({
                                      functionReturnValue:
                                        PreratingReturnValueSchemaMap[strategy.ruleName] ||
                                        z.string(),
                                      rating: z.enum(
                                        (currentParams.params.rating_buttons?.map(button =>
                                          button.value.toString(),
                                        ) as [string, ...string[]]) || ['-1', '1'],
                                      ),
                                    }),
                                  ),
                                })}
                                value={strategy}
                                onChange={(value: any) => {
                                  const newStrategies = [
                                    ...(currentParams.params.prerating_strategies || []),
                                  ];
                                  newStrategies[index] = value;
                                  setCurrentParams({
                                    ...currentParams,
                                    params: {
                                      ...currentParams.params,
                                      prerating_strategies: newStrategies,
                                    },
                                  });
                                }}
                              />
                              <Button
                                onClick={() => {
                                  const newStrategies = [
                                    ...(currentParams.params.prerating_strategies || []),
                                  ];
                                  newStrategies.splice(index, 1);
                                  setCurrentParams({
                                    ...currentParams,
                                    params: {
                                      ...currentParams.params,
                                      prerating_strategies: newStrategies,
                                    },
                                  });
                                }}
                                variant="neutral"
                                className="self-end"
                              >
                                Delete
                              </Button>
                            </div>
                          )}
                        </Draggable>
                      ),
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
            <Button
              onClick={() => {
                const newStrategies = [
                  ...(currentParams.params.prerating_strategies || []),
                  {
                    ruleName: Object.keys(PreratingStrategyMap)[0],
                    valueToRatingMapping: [],
                    params: {},
                  },
                ];
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, prerating_strategies: newStrategies },
                });
              }}
              variant="neutral"
              className="mt-4"
            >
              Add Prerating Strategy
            </Button>
          </>
        </>
      )}

      {tab === 'Advanced' && (
        <>
          <Typography.H3>Advanced Configuration</Typography.H3>
          <Typography.Paragraph2 className="">
            Configure advanced settings for the Process Supervision step.
          </Typography.Paragraph2>

          <BooleanInputBuilder
            name={'Allow user to skip entire turn'}
            existingValue={currentParams.params.allow_skip ?? false}
            updateProperty={(value: any) => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, allow_skip: value },
              });
            }}
            required={false}
            hint="If enabled, users can skip the entire turn"
          />

          <BooleanInputBuilder
            name={'Limit chat history'}
            existingValue={currentParams.params.limit_chat_history ?? false}
            updateProperty={(value: any) => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, limit_chat_history: value },
              });
            }}
            required={false}
            hint="If enabled, limit chat history to initial prompt and the most recent process supervision response"
          />

          <BooleanInputBuilder
            name={'Remove prompt templating for follow up steps'}
            existingValue={currentParams.params.remove_prompt_templating ?? false}
            updateProperty={(value: any) => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, remove_prompt_templating: value },
              });
            }}
            required={false}
            hint="If enabled, removes templating in the input for process supervision chunks. It will simply unchunk the chunks and pass it as a message."
          />

          <BooleanInputBuilder
            name={'Allow edits without regenerating remaining conversation'}
            existingValue={currentParams.params.enable_edit_without_reroll ?? false}
            updateProperty={(value: any) => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, enable_edit_without_reroll: value },
              });
            }}
            required={false}
            hint="If enabled, users can edit prior turns without resetting subsequent turns"
          />

          <BooleanInputBuilder
            name={'Output the first bad rating only'}
            existingValue={currentParams.params.output_first_bad_rating_only ?? false}
            updateProperty={(value: any) => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, output_first_bad_rating_only: value },
              });
            }}
            required={false}
            hint="If enabled, only the first bad rating will be output"
          />

          <BooleanInputBuilder
            name={'Require change on rewrites'}
            existingValue={currentParams.params.require_change_on_rewrites ?? true}
            updateProperty={(value: any) => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, require_change_on_rewrites: value },
              });
            }}
            required={false}
            hint="If enabled, rewrites will require a change from the original content (if not present, the rewrite is required)"
          />

          <StringInputBuilder
            name={'Remove regex matches from output'}
            existingValue={currentParams.params.remove_regex_matches}
            updateProperty={(value: string) => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, remove_regex_matches: value },
              });
            }}
            required={false}
            hint="Regex pattern to remove content/formatting artifacts from the output"
          />

          <BooleanInputBuilder
            name={'Re-request model'}
            existingValue={currentParams.params.re_request_model ?? true}
            updateProperty={(value: boolean) => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, re_request_model: value },
              });
            }}
            required={false}
            hint="Allow the model to be re-requested to continue generating steps"
          />
          {currentParams.params.re_request_model && (
            <BooleanInputBuilder
              name={'Skip Prompt Input for generating downstream steps'}
              existingValue={currentParams.params.can_skip_prompt_input_if_incomplete ?? false}
              updateProperty={(value: boolean) => {
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, can_skip_prompt_input_if_incomplete: value },
                });
              }}
              required={false}
              hint="Skips the prompt input step instead of auto-submitting a dummy user message. Check with Eng if this is applicable for this project's use case."
            />
          )}

          <Typography.H4>Ending turn early</Typography.H4>
          <p>
            Configure whether the contributor can end the current turn based on the ratings chosen
            for each chunk.
          </p>
          <StringInputBuilder
            name={'End After Ratings Title'}
            existingValue={currentParams.params.end_after_ratings_title || ''}
            placeholder={DEFAULT_PROCESS_SUPERVISION_END_AFTER_RATINGS_TITLE}
            updateProperty={(value: string) => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, end_after_ratings_title: value },
              });
            }}
            required={false}
            hint="Title to display when allowing contributor to end turn early"
          />
          <JSONInputBuilder
            name={'End After Rating IDs'}
            hint="Array of rating IDs after which the current turn should end"
            existingValue={currentParams.params.end_after_rating_ids || [-1]}
            updateProperty={(value: any) => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, end_after_rating_ids: value },
              });
            }}
            required={false}
          />

          <Typography.H4>Custom Prompt Template</Typography.H4>
          <p>
            Configure a custom prompt template for regeneration. You can use variables from the
            response context and the base supervision regeneration prompt.
          </p>
          <StringInputBuilder
            name={'Custom Prompt Template'}
            existingValue={currentParams.params.custom_prompt_template || ''}
            placeholder={`{{baseSupervisionRegenerationPrompt}} Your logic had the following issue: {{currentCorrectedChunkObject.some_extra_field}}`}
            updateProperty={(value: string) => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, custom_prompt_template: value },
              });
            }}
            required={false}
            hint="NOTE: this is HIGHLY EXPERIMENTAL and will likely be deprecated in favor of JSONata expressions soon. Use {{variableName}} to insert dynamic content. Available variables: baseSupervisionRegenerationPrompt (the default prompt for regeneration), correctedChunk.* (properties of the most recent chunk), allRawChunkContent (all raw chunk content), allCorrectChunkContent (the content of steps marked as correct or the rewrites of the rewritten steps), and currentCorrectedChunkObject (the original prompt)."
          />

          <Typography.H4>Customer Specific Configurations</Typography.H4>
          <p>Configure customer specific configurations for the process supervision step.</p>
          <ExperimentalFeature
            name="Flamingo Graph Strategy Configurations"
            details="Flamingo Graph Strategy configurations are experimental. Please consult with Lazarus Engineering before using."
            showInfoCondition={
              !!currentParams.params.flamingo_set_flags_on_incomplete_responses ||
              !!currentParams.params.flamingo_enable_partial_responses
            }
          >
            <Typography.H5>Flamingo Graph Strategy Configurations</Typography.H5>
            <BooleanInputBuilder
              name={'Flamingo Graph Strategy: Set incomplete/complete response flags'}
              existingValue={
                currentParams.params.flamingo_set_flags_on_incomplete_responses ?? false
              }
              required={false}
              hint="If enabled, the Flamingo Graph Strategy will set is_eot and is_complete flags accordingly"
              updateProperty={(value: boolean) => {
                setCurrentParams({
                  ...currentParams,
                  params: {
                    ...currentParams.params,
                    flamingo_set_flags_on_incomplete_responses: value,
                  },
                });
              }}
            />
            <BooleanInputBuilder
              name={'Flamingo Graph Strategy: handle partial responses'}
              existingValue={currentParams.params.flamingo_enable_partial_responses ?? false}
              required={false}
              hint="If enabled, the process supervision will gracefully handle partial responses from Flamingo Graph Strategy"
              updateProperty={(value: boolean) => {
                setCurrentParams({
                  ...currentParams,
                  params: {
                    ...currentParams.params,
                    flamingo_enable_partial_responses: value,
                  },
                });
              }}
            />
          </ExperimentalFeature>
        </>
      )}

      {tab === ConfigTabs.ConditionalVisibility && (
        <>
          <Typography.H3>Conditional Visibility</Typography.H3>
          <Typography.Paragraph2 className="">
            Configure when this step should be visible based on conditions.
          </Typography.Paragraph2>
          <ConditionalStepConfig
            currentParams={currentParams}
            setCurrentParams={setCurrentParams}
            previousParams={previousParams}
            taskParams={taskParams}
          />
        </>
      )}
    </div>
  );
}
